<template>
  <asd20-app-template
    :keywords.sync="keywords"
    :version="`${version}`"
    :sidebar-badge="totalFiltersApplied"
    :back-link="cleanedReferrer"
    :back-label="cleanedReferrer.replace(/https?:\/\//, '')"
    :sidebar-icon="desktop ? '' : 'calendar'"
    sidebar-title="Calendar Options"
    title="Calendar"
    icon="calendar"
    role="main"
  >
    <template v-slot:toolbar-start>
      <asd20-select-input
        :value="selectedOrganizationId"
        :items="organizations"
        item-label="title"
        item-value="id"
        hide-label
        label="Organization Selector"
        @input="onSelectOrganizationId"
      >
        <template #prefix>
          <img
            v-if="selectedOrganization"
            :src="
              selectedOrganization.logoImageUrl
                ? selectedOrganization.logoImageUrl
                : null
            "
            alt="School logo"
            class="school-logo"
            style="width: auto; height: 2em; margin-left: 1rem;"
          >
        </template>
      </asd20-select-input>
    </template>

    <!-- <asd20-button
      v-if="totalFiltersApplied && $mq !== 'sm' && $mq !== 'md'"
      slot="sidebar-title"
      class="clearButton"
      style="order: 3;"
      transparent
      horizontal
      size="sm"
      icon="danger"
      label="Clear Selections"
      @click.native="clearFilters"
    /> -->

    <asd20-button
      v-if="totalFiltersApplied"
      slot="sidebar-title"
      class="clearButton"
      style="order: 3;"
      transparent
      horizontal
      size="sm"
      icon="danger"
      label="Clear Selections"
      @click.native="clearFilters"
    />

    <Asd20EventFilters slot="sidebar" />

    <aside
      slot="sidebar"
      class="footer-actions">
      <asd20-button
        id="pdfCalendars-button"
        label="Annual Calendars"
        icon="calendar"
        size="sm"
        centered
        horizontal
        bordered
        @click.native="pdfModalOpen = true"
      />
      <asd20-button
        id="multipleSchools-button"
        label="Multiple Schools"
        icon="school"
        size="sm"
        centered
        horizontal
        bordered
        @click.native="calendarLocationsModalOpen = true"
      />
      <asd20-button
        id="subscribe-button"
        label="Subscribe with iCal"
        icon="calendar-alt"
        size="sm"
        centered
        horizontal
        bordered
        @click.native="subscriptionModalOpen = true"
      />
    </aside>

    <template>
      <main>
        <div class="month-toolbar">
          <div class="month-toolbar-wrapper">
            <Asd20Button
              id="previous-button"
              :icon-angle="-90"
              aria-label="previous month"
              icon="chevron"
              size="sm"
              transparent
              centered
              style="order: 1;"
              @click.native="previousMonth"
            />
            <Asd20MonthSelect
              v-model="monthAndYear"
              style="order: 2;" />
            <Asd20Button
              id="next-button"
              :icon-angle="90"
              aria-label="next month"
              icon="chevron"
              size="sm"
              transparent
              centered
              style="order: 3;"
              @click.native="nextMonth"
            />
          </div>
          <div class="view-toggle">
            <asd20-checkbox
              :value="view === 'calendar'"
              class="viewToggle"
              label="List View"
              description="Grid View"
              style="order: 3; flex-grow: 0;"
              @input="toggleView"
            />
          </div>
        </div>
        <div class="calendar-wrap">
          <asd20-event-list
            v-if="view === 'list'"
            :events="parsedEvents"
            large
            hide-past-events
          />
          <asd20-calendar
            v-touch:swipe.right="previousMonth"
            v-touch:swipe.left="previousMonth"
            v-if="view === 'calendar'"
            :events="parsedEvents"
            :month="month"
            :year="year"
          />

          <Asd20Loader
            v-show="loading"
            size="lg" />
        </div>
      </main>
    </template>

    <asd20-modal
      :open="pdfModalOpen"
      class="pdf-calendar-list"
      title="Printable Yearly Calendars"
      dismissable
      windowed
      @dismiss="pdfModalOpen = false"
    >
      <asd20-viewport
        class="pdf-list"
        scrollable
        padded>
        <p>
          The primary source for district calendar information is the Academy
          District 20 Calendar App at
          <a href="https://calendar.asd20.org">calendar.asd20.org</a>. The
          annual calendars listed here contain a limited set of events which
          change infrequently. We recommend using the calendar app for the most
          up-to-date information.
        </p>
        <hr >
        <asd20-button
          class="pdf-button"
          label="2024-25 District Calendar (PDF)"
          icon="files"
          size="sm"
          link="https://asd20websitestorage.blob.core.windows.net/website-files/Academy-District-20/ASD20%20Calendar%202024-25.pdf"
          centered
          horizontal
          bordered
        />
        <asd20-button
          class="pdf-button"
          label="2025-26 District Calendar (PDF)"
          icon="files"
          size="sm"
          link="https://asd20websitestorage.blob.core.windows.net/website-files/Academy-District-20/ASD20%20Calendar%202025-26.pdf"
          centered
          horizontal
          bordered
        />
        <asd20-button
          class="pdf-button"
          label="2026-27 District Calendar (PDF)"
          icon="files"
          size="sm"
          link="https://asd20websitestorage.blob.core.windows.net/website-files/Academy-District-20/ASD20%20Calendar%202026-27.pdf"
          centered
          horizontal
          bordered
        />
        <asd20-button
          class="pdf-button"
          label="2027-28 District Calendar (PDF)"
          icon="files"
          size="sm"
          link="https://asd20websitestorage.blob.core.windows.net/website-files/Academy-District-20/ASD20%20Calendar%202027-28.pdf"
          centered
          horizontal
          bordered
        />
        <hr >
        <h3>New Annual Calendar Format</h3>
        <p>
          A new Annual Calendar format is being introduced below, and will
          replace the calendars above. These calendars can be viewed on your
          device or you can download a PDF via a button at the upper right of
          the calendar.
        </p>
        <hr >
        <div v-if="isHighSchool">
          <h4>{{ selectedOrganization.abbreviation }} Calendars</h4>

          <asd20-button
            :label="
              `2024-25 ${selectedOrganization.abbreviation} Annual Calendar`
            "
            class="pdf-button"
            icon="calendar"
            size="sm"
            centered
            horizontal
            bordered
            stacked
            @click.native="
              setSelectedOrgAndNavigate(selectedOrganization.id, {
                name: 'HighSchoolAnnualCalendar',
                params: { abbreviation: selectedOrganization.abbreviation },
              })
            "
          />

          <hr >
        </div>

        <h4>District Calendars</h4>

        <asd20-button
          class="pdf-button"
          label="2024-25 Annual District Calendar"
          icon="calendar"
          size="sm"
          centered
          horizontal
          bordered
          @click.native="
            setSelectedOrgAndNavigate(
              '26eaf390-d8ab-11e9-a3a8-5de5bba4f125', // District Org ID
              {
                name: 'DistrictAnnualCalendar',
                params: { abbreviation: 'ASD20', year: 2024 },
              }
            )
          "
        />

        <asd20-button
          class="pdf-button"
          label="2025-26 Annual District Calendar"
          icon="calendar"
          size="sm"
          centered
          horizontal
          bordered
          @click.native="
            setSelectedOrgAndNavigate(
              '26eaf390-d8ab-11e9-a3a8-5de5bba4f125', // District Org ID
              {
                name: 'DistrictAnnualCalendar',
                params: { abbreviation: 'ASD20', year: 2025 },
              }
            )
          "
        />

        <asd20-button
          class="pdf-button"
          label="2026-27 Annual District Calendar"
          icon="calendar"
          size="sm"
          centered
          horizontal
          bordered
          @click.native="
            setSelectedOrgAndNavigate(
              '26eaf390-d8ab-11e9-a3a8-5de5bba4f125', // District Org ID
              {
                name: 'DistrictAnnualCalendar',
                params: { abbreviation: 'ASD20', year: 2026 },
              }
            )
          "
        />
      </asd20-viewport>
    </asd20-modal>

    <asd20-modal
      :open="calendarLocationsModalOpen"
      title="Add or Remove Schools from the Calendar View"
      dismissable
      windowed
      @dismiss="calendarLocationsModalOpen = false"
    >
      <asd20-viewport
        scrollable
        padded>
        <asd20-checkbox-list
          :options="organizationOptions"
          :checked-values="includedOrganizationIds"
          @input="includedOrganizationIds = $event"
        />
      </asd20-viewport>
    </asd20-modal>

    <asd20-modal
      :open="subscriptionModalOpen"
      title="Subscribe to our Calendars Using iCal"
      dismissable
      windowed
      @dismiss="subscriptionModalOpen = false"
    >
      <asd20-viewport
        class="subscription-list"
        scrollable
        padded>
        <p>
          Your device determines how an iCal feed is set up. Subscribing may be
          automatic, or you may be asked to enter the link address (url) for the
          calendar.
        </p>
        <p>
          Use the <b>Subscribe</b> button to add events to your device, or use
          the <b>Copy to Clipboard</b> button if you need to add the url to your
          device's calendar app manually.
        </p>
        <p>
          Once you've subscribed to a calendar, syncronization of events,
          whether manual or automatic, will vary based on your device settings.
          Unsubscribing or deleting these events is also managed on your device.
        </p>
        <hr >
        <!-- <div
          v-if="
            calendarOptionsByOrganizationAndCategory.length &&
              calendarOptionsByOrganizationAndCategory[0]
                .categorizedCalendarCategories.length > 0
          "
        >
          <asd20-accordion
            v-for="(item, index0) of calendarOptionsByOrganizationAndCategory[0]
              .categorizedCalendarCategories"
            :key="index0"
            :label="item.text"
            class="categorized-list"
          >
            <div
              v-for="(item,
              index1) in calendarOptionsByOrganizationAndCategory[0]
                .categorizedCalendarCategories[0].calendarOptions"
              :key="index1"
              class="subscriptionActions"
            >
              <asd20-list-item
                :key="index1"
                :link="item.url"
                :label="`Subscribe: ${item.text}`"
                class="subscribe"
                actionable
              />
              <asd20-button
                slot="actions"
                :class="{
                  'copy-to-clipboard-copied': index1 === indexOfCopiedItem1,
                }"
                :label="
                  index1 === indexOfCopiedItem1 ? 'Copied' : 'Copy to Clipboard'
                "
                class="copy-to-clipboard"
                horizontal
                size="sm"
                @click.native="copyToClipboard1(item.url, index1)"
              /></div
          ></asd20-accordion>
        </div>

        <div v-if="calendarOptionsByOrganizationAndCategory.length">
          <div
            v-for="(item, index2) in calendarOptionsByOrganizationAndCategory[0]
              .uncategorizedCalendarOptions"
            :key="index2"
            class="subscriptionActions"
          >
            <asd20-list-item
              :link="item.url"
              :label="`Subscribe: ${item.text}`"
              class="subscribe"
              actionable
            />
            <asd20-button
              slot="actions"
              :class="{
                'copy-to-clipboard-copied': index2 === indexOfCopiedItem2,
              }"
              :label="
                index2 === indexOfCopiedItem2 ? 'Copied' : 'Copy to Clipboard'
              "
              class="copy-to-clipboard"
              horizontal
              bordered
              @click.native="copyToClipboard2(item.url, index2)"
            />
          </div>
        </div> -->

        <div
          v-for="(orgGroup, indexOrg) in selectedCalendarOptionsByOrganization"
          :key="indexOrg"
        >
          <asd20-accordion
            :label="
              orgGroup.organization.abbreviation.replace(
                'ASD20',
                'District Wide'
              )
            "
            class="categorized-list"
          >
            <!-- Categorized calendars -->
            <template
              v-for="(category,
                      indexCat) in orgGroup.categorizedCalendarCategories"
            >
              <asd20-accordion
                :key="`cat-${indexCat}`"
                :label="category.text"
                class="categorized-list"
              >
                <div
                  v-for="(item, indexItem) in category.calendarOptions"
                  :key="`cat-${indexCat}-item-${indexItem}`"
                  class="subscriptionActions"
                >
                  <asd20-list-item
                    :link="item.url"
                    :label="`Subscribe: ${item.text}`"
                    class="subscribe"
                    actionable
                  />
                  <asd20-button
                    slot="actions"
                    :class="{
                      'copy-to-clipboard-copied':
                        indexItem === indexOfCopiedItem2,
                    }"
                    :label="
                      indexItem === indexOfCopiedItem2
                        ? 'Copied'
                        : 'Copy to Clipboard'
                    "
                    class="copy-to-clipboard"
                    horizontal
                    bordered
                    @click.native="copyToClipboard2(item.url, indexItem)"
                  />
                </div>
              </asd20-accordion>
            </template>

            <!-- Uncategorized calendars -->
            <div
              v-for="(item, indexU) in orgGroup.uncategorizedCalendarOptions"
              :key="`uncat-${indexU}`"
              class="subscriptionActions"
            >
              <asd20-list-item
                :link="item.url"
                :label="`Subscribe: ${item.text}`"
                class="subscribe"
                actionable
              />
              <asd20-button
                slot="actions"
                :class="{
                  'copy-to-clipboard-copied': indexU === indexOfCopiedItem2,
                }"
                :label="
                  indexU === indexOfCopiedItem2 ? 'Copied' : 'Copy to Clipboard'
                "
                class="copy-to-clipboard"
                horizontal
                bordered
                @click.native="copyToClipboard2(item.url, indexU)"
              />
            </div>
          </asd20-accordion>
        </div>
      </asd20-viewport>
    </asd20-modal>
    <div class="annual-calendar-modal">
      <asd20-modal
        :open="view === 'annual'"
        :title="annualCalendarTitle"
        dismissable
        @dismiss="closeAnnualModal"
      >
        <asd20-viewport
          class="annual-calendar-viewport"
          scrollable>
          <div class="annual-calendar-controls">
            <div class="print-controls">
              <asd20-button
                class="print-button"
                transparent
                horizontal
                bordered
                size="sm"
                icon="printer"
                label="PDF/Print (Landscape)"
                @click.native="printStandard"
              />
              <asd20-button
                class="poster-print-button"
                transparent
                horizontal
                bordered
                size="sm"
                icon="printer"
                label="PDF/ Print (Portrait)"
                @click.native="printPoster"
              />
            </div>
            <span class="title-and-logo">{{ annualCalendarTitle }}</span>
            <asd20-logo
              v-if="
                selectedOrganization &&
                  ![
                    'Academy District 20',
                    'Academy District 20 Insider',
                  ].includes(selectedOrganization.title)
              "
              :title="mapOrganizationTitle"
              :subtitle="mapOrganizationSubtitle"
              :image="selectedOrganization.logoImageUrl"
              :link="selectedOrganization.website"
              size="sm"
              bordered
              tagline
              reversed
              wrappable
            />

            <asd20-district-logo
              v-if="
                selectedOrganization &&
                  [
                    'Academy District 20',
                    'Academy District 20 Insider',
                  ].includes(selectedOrganization.title)
              "
            />
          </div>
          <div class="print-wrapper">
            <asd20-annual-calendar
              :events="parsedAnnualEvents"
              :start-year="selectedAnnualYear"
              :poster-mode="posterMode"
              :selected-organization="selectedOrganization"
            />
          </div>
          <div class="bottom-logo">
            <asd20-logo
              v-if="
                selectedOrganization &&
                  ![
                    'Academy District 20',
                    'Academy District 20 Insider',
                  ].includes(selectedOrganization.title)
              "
              :title="mapOrganizationTitle"
              :subtitle="mapOrganizationSubtitle"
              :image="selectedOrganization.logoImageUrl"
              :link="selectedOrganization.website"
              size="sm"
              bordered
              tagline
              reversed
              wrappable
            />

            <asd20-district-logo
              v-if="
                selectedOrganization &&
                  [
                    'Academy District 20',
                    'Academy District 20 Insider',
                  ].includes(selectedOrganization.title)
              "
            />
          </div>
        </asd20-viewport>
      </asd20-modal>
    </div>
  </asd20-app-template>
</template>

<script>
// Helpers
import { get, sync } from 'vuex-pathify'
import syncronizedQueryParameters from '../../mixins/syncronizedQueryParameters'

// Components
import Asd20MonthSelect from '../atoms/Asd20MonthSelect'
import Asd20EventFilters from '../organisms/Asd20EventFilters'
import Asd20AnnualCalendar from '../organisms/Asd20AnnualCalendar'

import Asd20AppTemplate from '@asd20/ui/src/components/templates/Asd20AppTemplate'
import Asd20Calendar from '@asd20/ui/src/components/organisms/Asd20Calendar'
import Asd20EventList from '@asd20/ui/src/components/organisms/Asd20EventList'
import Asd20DistrictLogo from '@asd20/ui/src/components/atoms/Asd20DistrictLogo'
import Asd20Modal from '@asd20/ui/src/components/molecules/Asd20Modal'
import Asd20Checkbox from '@asd20/ui/src/components/atoms/Asd20Checkbox'
import Asd20CheckboxList from '@asd20/ui/src/components/molecules/Asd20CheckboxList'
import Asd20Loader from '@asd20/ui/src/components/molecules/Asd20Loader'
import Asd20Viewport from '@asd20/ui/src/components/atoms/Asd20Viewport'
import Asd20SelectInput from '@asd20/ui/src/components/atoms/Asd20SelectInput'
import Asd20ListItem from '@asd20/ui/src/components/molecules/Asd20ListItem'
import Asd20List from '@asd20/ui/src/components/organisms/Asd20List'
import Asd20Accordion from '@asd20/ui/src/components/molecules/Asd20Accordion'
import Asd20Logo from '@asd20/ui/src/components/atoms/Asd20Logo'
import {
  organizationLevel,
  organizationName,
} from '@asd20/ui/src/helpers/organization.js'
import { version } from '../../../package.json'
import { classifyCalendarEvent } from '../../helpers/classifyCalendarEvent.js'

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export default {
  name: 'HomePage',

  components: {
    Asd20MonthSelect,
    Asd20EventFilters,
    Asd20AnnualCalendar,
    Asd20AppTemplate,
    Asd20Calendar,
    Asd20EventList,
    Asd20Checkbox,
    Asd20CheckboxList,
    Asd20DistrictLogo,
    Asd20Modal,
    Asd20Viewport,
    Asd20SelectInput,
    Asd20Loader,
    Asd20ListItem,
    Asd20List,
    Asd20Accordion,
    Asd20Logo,
  },

  mixins: [syncronizedQueryParameters],

  data: () => ({
    version,
    pdfModalOpen: false,
    calendarLocationsModalOpen: false,
    subscriptionModalOpen: false,
    copied: false,
    indexOfCopiedItem1: null,
    indexOfCopiedItem2: null,
    desktop: false,
    selectedAnnualYear: 2024,
    syncronizedQueryParameters: [
      {
        componentKey: 'includedOrganizationIds',
        queryKey: 'includedOrganizationIds',
        forceArray: true,
      },
      {
        componentKey: 'selectedCalendarIds',
        queryKey: 'selectedCalendarIds',
        forceArray: true,
      },
    ],
    localAnnualEvents: [],
    posterMode: false,
    highSchoolAbbreviations: ['aahs', 'dcchs', 'lhs', 'pchs', 'rhs'],
    previousAbbreviation: null,
  }),

  computed: {
    loading: get('loading'),
    referrer: get('referrer'),
    totalFiltersApplied: get('totalFiltersApplied'),
    events: get('events'),
    annualEvents: get('annualEvents'),
    calendarOptionsByOrganizationAndCategory: get(
      'calendarOptionsByOrganizationAndCategory'
    ),
    organizations: get('organizations'),
    organizationOptions: get('organizationOptions'),
    selectedOrganizationId: sync('selectedOrganizationId'),
    includedOrganizationIds: sync('includedOrganizationIds'),
    selectedOrganization: get('selectedOrganization'),
    selectedCalendarIds: sync('selectedCalendarIds'),
    calendarSubscriptions: get('calendarSubscriptions'),
    view: sync('view'),
    keywords: sync('keywords'),
    start: get('startDate'),
    month: sync('month'),
    year: sync('year'),
    monthAndYear: {
      get() {
        return `${months[this.month]} ${this.year}`
      },
      set(value) {
        this.year = Number(value.split(' ')[1])
        this.month = months.indexOf(value.split(' ')[0])
      },
    },
    searchPayload: get('searchPayload'),
    cleanedReferrer() {
      const calendarHost = window.location.origin
      const ref = this.referrer

      if (ref && !ref.startsWith(calendarHost)) {
        return ref
      } else {
        // No valid external referrer; default to district site
        return 'https://www.asd20.org'
      }
    },

    parsedEvents() {
      // console.log('events: ', this.events)
      return this.events
        .map(e => ({
          ...e,
          start: e.start ? e.start : null,
          end: e.end ? e.end : null,
        }))
        .reduce((a, e) => {
          const hasDuplicate =
            a.filter(
              ae =>
                ae.summary === e.summary &&
                ae.start === e.start &&
                ae.end === e.end &&
                ae.calendarId === e.calendarId
            ).length > 0
          if (!hasDuplicate) a.push(e)
          return a
        }, [])
    },
    parsedAnnualEvents() {
      const events = this.localAnnualEvents.length
        ? this.localAnnualEvents
        : this.annualEvents
      return events
        .map(e => ({
          ...e,
          start: e.start ? e.start : null,
          end: e.end ? e.end : null,
          calendarColor: classifyCalendarEvent(e.summary, e.calendarColor),
        }))
        .reduce((a, e) => {
          const hasDuplicate =
            a.filter(
              ae =>
                ae.summary === e.summary &&
                ae.start === e.start &&
                ae.end === e.end &&
                ae.calendarId === e.calendarId
            ).length > 0
          if (!hasDuplicate) a.push(e)
          return a
        }, [])
    },
    annualCalendarTitle() {
      if (!this.selectedAnnualYear) return 'Annual Calendar View'
      const nextYear = this.selectedAnnualYear + 1
      const shortNextYear = nextYear.toString().slice(-2)
      return `${this.selectedAnnualYear} - ${shortNextYear} Annual Calendar`
    },
    isHighSchool() {
      if (!this.selectedOrganization) return false
      return this.highSchoolAbbreviations.includes(
        this.selectedOrganization.abbreviation.toLowerCase()
      )
    },
    selectedCalendarOptionsByOrganization() {
      const selected = this.selectedCalendarIds || []
      const grouped = []

      this.calendarOptionsByOrganizationAndCategory.forEach(org => {
        const categorizedCalendarCategories = []

        org.categorizedCalendarCategories.forEach(cat => {
          const filteredOptions = cat.calendarOptions.filter(option =>
            selected.includes(option.value)
          )

          if (filteredOptions.length > 0) {
            categorizedCalendarCategories.push({
              ...cat,
              calendarOptions: filteredOptions,
            })
          }
        })

        const uncategorizedCalendarOptions = org.uncategorizedCalendarOptions.filter(
          option => selected.includes(option.value)
        )

        if (
          categorizedCalendarCategories.length > 0 ||
          uncategorizedCalendarOptions.length > 0
        ) {
          grouped.push({
            organization: org.organization,
            categorizedCalendarCategories,
            uncategorizedCalendarOptions,
          })
        }
      })

      return grouped
    },
  },

  watch: {
    searchPayload: {
      handler: function() {
        this.fetchData()
      },
    },
    '$route.params.abbreviation': {
      handler(newVal) {
        if (newVal !== this.previousAbbreviation) {
          this.previousAbbreviation = newVal
          this.applyRouteParams()
          this.fetchData()
        }
      },
    },
  },

  // Trigger fetch on route enter
  beforeRouteEnter(from, to, next) {
    next(async vm => {
      vm.previousAbbreviation = to.params.abbreviation || null
      await vm.applyRouteParams()
      vm.fetchData()
    })
  },
  mounted() {
    this.desktop = window.innerWidth >= 1024
    // this.zoomed = window.innerHeight <= 500
    window.addEventListener('resize', this.handleResize)
  },

  methods: {
    // onSelectOrganizationId(id) {
    //   this.$store.dispatch('clearFilters')
    //   this.includedOrganizationIds = []
    //   this.selectedOrganizationId = id
    // },
    onSelectOrganizationId(id) {
      this.$store.dispatch('clearFilters')

      const district_org_id = '26eaf390-d8ab-11e9-a3a8-5de5bba4f125'
      const district_calendar_ids = [
        '2002ca04-bd3b-dd84-1e4e-576d878dd497',
        '38e42670-b6eb-11ee-b0fb-99183002509c',
      ]
      const exclude_district_add_ins = [
        '26eaf390-d8ab-11e9-a3a8-5de5bba4f125', // District
        '35db5d9b-a1a4-4299-77c1-4cfafb2c6486', // HSA
        'e0f7c870-1f4d-34e2-934c-28b991f697ae', // VHS
        '50664046-8b15-8dc7-8cd1-94950d4324be', // VMS
      ]
      const calendarForElementary = '0ff78a00-c178-11ec-a412-fd5f1ae617e2'
      const calendarForMiddle = 'ef106e90-0b30-11ec-b38f-953cc4383d45'
      const calendarForHigh = 'a49033ae-db20-d53a-d5c3-737815b4aa34'

      this.selectedOrganizationId = id
      this.includedOrganizationIds = [id]

      // Start with calendars owned by the selected org
      let calendarIds = (this.$store.state.calendarSubscriptions || [])
        .filter(s => s.ownerOrganizationId === this.selectedOrganizationId)
        .map(s => s.id)

      // Only add district calendars if not excluded
      if (!exclude_district_add_ins.includes(this.selectedOrganizationId)) {
        calendarIds = calendarIds.concat(district_calendar_ids)

        // Add district to included orgs if not already there
        if (!this.includedOrganizationIds.includes(district_org_id)) {
          this.includedOrganizationIds = [
            ...this.includedOrganizationIds,
            district_org_id,
          ]
        }
        // Add level-specific calendar based on educationLevels
        if (
          this.selectedOrganization &&
          Array.isArray(this.selectedOrganization.educationLevels)
        ) {
          const levels = this.selectedOrganization.educationLevels.map(level =>
            level.toLowerCase()
          )

          if (levels.includes('elementary')) {
            calendarIds.push(calendarForElementary)
          }
          if (levels.includes('middle')) {
            calendarIds.push(calendarForMiddle)
          }
          if (levels.includes('high')) {
            calendarIds.push(calendarForHigh)
          }
        }
      }

      console.log('Selecting all calendarIds for organization:', calendarIds)
      this.$store.dispatch('setSelectedCalendarIds', calendarIds)
    },
    async applyRouteParams() {
      // Fetch the necessary data
      await this.$store.dispatch('getOrganizations')
      await this.$store.dispatch('getCalendarSubscriptions')

      const query = this.$route.query
      // const selectedOrganizationIdFromURL = query.includedOrganizationIds
      let selectedOrganizationIdFromURL = query.includedOrganizationIds
      if (Array.isArray(selectedOrganizationIdFromURL)) {
        selectedOrganizationIdFromURL = selectedOrganizationIdFromURL[0]
      }
      const selectedCalendarIdsFromURL = query.selectedCalendarIds
      const abbreviation = this.$route.params.abbreviation // School abbreviation in the URL

      console.log(
        'Selected organization from URL:',
        selectedOrganizationIdFromURL
      )
      console.log('Selected calendars from URL:', selectedCalendarIdsFromURL)
      console.log('Abbreviation from URL:', abbreviation)

      // Find the organization based on the abbreviation
      let orgId = this.organizations[0].id // Default to the first org

      if (abbreviation) {
        const org = this.organizations.find(
          o => o.abbreviation.toLowerCase() === abbreviation.toLowerCase()
        )
        if (org) {
          orgId = org.id
          console.log(
            `Found organization for abbreviation '${abbreviation}':`,
            orgId
          )
        } else {
          console.log(
            `No organization found for abbreviation '${abbreviation}', using default.`
          )
        }
      }

      // Set the selected organization (either from URL query or abbreviation)
      if (!this.selectedOrganizationId) {
        this.selectedOrganizationId = selectedOrganizationIdFromURL || orgId
      }

      // If selected calendar ids are provided in the URL, use them instead of selecting all calendars
      if (selectedCalendarIdsFromURL) {
        const selectedCalendarsArray = Array.isArray(selectedCalendarIdsFromURL)
          ? selectedCalendarIdsFromURL
          : [selectedCalendarIdsFromURL]

        this.$store.dispatch('setSelectedCalendarIds', selectedCalendarsArray)
        console.log(
          'Setting selected calendars from URL:',
          selectedCalendarsArray
        )
      } else {
        // If no calendarIds in the URL, select all calendars for the organization
        // const calendarIds = (this.$store.state.calendarSubscriptions || [])
        //   .filter(s => s.ownerOrganizationId === this.selectedOrganizationId)
        //   .map(s => s.id)

        // console.log('Selecting all calendarIds for organization:', calendarIds)
        // this.$store.dispatch('setSelectedCalendarIds', calendarIds)

        // If no calendarIds in the URL, select all calendars for the organization
        const calendarSubscriptions =
          this.$store.state.calendarSubscriptions || []

        // Get the calendars owned by the selected org
        let calendarIds = calendarSubscriptions
          .filter(s => s.ownerOrganizationId === this.selectedOrganizationId)
          .map(s => s.id)

        // If selected org is NOT the district, add the 2 district calendars and district org ID
        const district_org_id = '26eaf390-d8ab-11e9-a3a8-5de5bba4f125'
        const district_calendar_ids = [
          '2002ca04-bd3b-dd84-1e4e-576d878dd497',
          '38e42670-b6eb-11ee-b0fb-99183002509c',
        ]

        if (this.selectedOrganizationId !== district_org_id) {
          calendarIds = [...new Set([...calendarIds, ...district_calendar_ids])]
          this.includedOrganizationIds = [
            this.selectedOrganizationId,
            district_org_id,
          ]
        } else {
          // Just the district org
          this.includedOrganizationIds = [district_org_id]
        }

        console.log('Selecting all calendarIds for organization:', calendarIds)
        this.$store.dispatch('setSelectedCalendarIds', calendarIds)
      }
    },

    // Load data needed for the view
    async fetchData() {
      this.$store.dispatch('getFacets')
      await this.$store.dispatch('queryEvents')
    },
    async fetchAnnualData(year) {
      await this.$store.dispatch('queryAnnualEvents', year)
    },
    clearFilters() {
      this.$store.dispatch('clearFilters')
    },
    selectAll() {
      const calendarIds = (this.$store.state.calendarSubscriptions || [])
        .filter(s => s.ownerOrganizationId === this.selectedOrganizationId)
        .map(s => s.id)

      console.log('Selecting all calendarIds for organization:', calendarIds)
      this.$store.dispatch('setSelectedCalendarIds', calendarIds)
    },

    queryEvents() {
      this.$store.dispatch('queryEvents')
    },

    nextMonth() {
      if (this.month < 11) {
        this.month++
      } else {
        const year = new Date().getFullYear()
        if (this.year < year + 1) {
          this.month = 0
          this.year++
        }
      }
    },

    previousMonth() {
      if (this.month > 0) {
        this.month--
      } else {
        const year = new Date().getFullYear()
        if (this.year > year - 1) {
          this.month = 11
          this.year--
        }
      }
    },
    copyToClipboard1(url, index1) {
      navigator.clipboard.writeText(url)
      this.indexOfCopiedItem1 = index1
      setTimeout(() => {
        this.indexOfCopiedItem1 = null
      }, 1500)
    },
    copyToClipboard2(url, index2) {
      navigator.clipboard.writeText(url)
      this.indexOfCopiedItem2 = index2
      setTimeout(() => {
        this.indexOfCopiedItem2 = null
      }, 1500)
    },
    handleResize() {
      this.desktop = window.innerWidth >= 1024
      // this.zoomed = window.innerHeight <= 500
    },
    toggleView() {
      this.view = this.view === 'calendar' ? 'list' : 'calendar'
    },
    mapOrganizationTitle() {
      return this.selectedOrganization
        ? organizationName(this.selectedOrganization.title)
        : ''
    },
    mapOrganizationSubtitle() {
      return this.selectedOrganization
        ? organizationLevel(this.selectedOrganization.title)
        : ''
    },
    async openAnnualCalendarWithFilters(year) {
      this.selectedOrganizationId = '26eaf390-d8ab-11e9-a3a8-5de5bba4f125'
      this.includedOrganizationIds = ['26eaf390-d8ab-11e9-a3a8-5de5bba4f125']
      this.$store.dispatch('setSelectedCalendarIds', [
        '2002ca04-bd3b-dd84-1e4e-576d878dd497',
        '0ff78a00-c178-11ec-a412-fd5f1ae617e2',
        'ef106e90-0b30-11ec-b38f-953cc4383d45',
        '38e42670-b6eb-11ee-b0fb-99183002509c',
      ])

      this.view = 'annual'
      this.selectedAnnualYear = year
      this.month = 0

      // Load static JSON if year is in the future
      if (year === 2025) {
        const response = await fetch('/data/2025-26annual.json')
        this.localAnnualEvents = await response.json()
      } else if (year === 2026) {
        const response = await fetch('/data/2026-27annual.json')
        this.localAnnualEvents = await response.json()
      } else {
        this.localAnnualEvents = []
        this.fetchAnnualData(year)
      }
    },
    setPageSize(size, margin) {
      this.removeInjectedPageStyle()

      const style = document.createElement('style')
      style.id = 'injected-print-page-style'
      style.innerHTML = `@page { size: ${size}; margin: ${margin}; }`

      document.head.appendChild(style)
    },

    removeInjectedPageStyle() {
      const existing = document.getElementById('injected-print-page-style')
      if (existing) existing.remove()
    },
    closeAnnualModal() {
      this.view = 'calendar'

      // Reset to today's date
      const today = new Date()
      this.month = today.getMonth()
      this.year = today.getFullYear()

      // Re-enable all calendars for selected org
      const calendarIds = (this.$store.state.calendarSubscriptions || [])
        .filter(s => s.ownerOrganizationId === this.selectedOrganizationId)
        .map(s => s.id)

      this.$store.dispatch('setSelectedCalendarIds', calendarIds)
    },
    cloneToHiddenPage() {
      // Remove existing clone if it exists
      const existing = document.getElementById('print-container')
      if (existing) existing.remove()

      // Create print container
      const printContainer = document.createElement('div')
      printContainer.id = 'print-container'
      printContainer.style.position = 'fixed'
      printContainer.style.top = '0'
      printContainer.style.left = '0'
      printContainer.style.width = '100vw'
      printContainer.style.height = '100vh'
      printContainer.style.zIndex = '9999'
      printContainer.style.background = 'white'
      printContainer.style.overflow = 'auto'

      // Clone the annual calendar content
      const content = this.$el.querySelector('.print-wrapper')
      if (content) {
        const cloned = content.cloneNode(true)
        printContainer.appendChild(cloned)
      }

      document.body.appendChild(printContainer)
    },
    printStandard() {
      this.setPageSize('letter landscape', '0.25in')
      this.posterMode = false
      this.$nextTick(() => {
        this.cloneToHiddenPage()
        this.$nextTick(() => {
          window.print()
          setTimeout(() => {
            this.cleanupAfterPrint()
          }, 1000)
        })
      })
    },

    printPoster() {
      this.setPageSize('ledger portrait', '0.5in')
      this.posterMode = true
      this.$nextTick(() => {
        this.cloneToHiddenPage()
        this.$nextTick(() => {
          window.print()
          setTimeout(() => {
            this.posterMode = false
            this.cleanupAfterPrint()
          }, 1000)
        })
      })
    },
    cleanupAfterPrint() {
      this.removeInjectedPageStyle()
      const printContainer = document.getElementById('print-container')
      if (printContainer) printContainer.remove()
    },
    setSelectedOrgAndNavigate(orgId, route) {
      this.$store.commit('SET_SELECTED_ORGANIZATION_ID', orgId)
      this.$router.push(route)
    },
  },
}
</script>

<style lang="scss">
@import '@asd20/ui/src/design/_variables.scss';

.asd20-app-template {
  .asd20-icon {
    fill: #95b8eb;
  }
  input.subscription-link {
    min-width: min-content;
    min-width: 60%;
    max-width: 100%;
    padding: space(0.5);
    font-size: 0.5rem;
  }
  &__mobile-layout {
    .asd20-modal__header h2 {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
  .asd20-app-template__toolbar {
    border-bottom: 2px solid var(--color__tertiary);
    justify-content: space-between;
    .asd20-button {
      margin-right: 1rem;
      background: #ecf0f5;
      .asd20-badge {
        top: 0;
        right: 0;
      }
    }
  }
  .asd20-event-filters {
    border-bottom: 2px solid var(--color__tertiary);
  }
  .clearButton {
    // width: min-content;
    padding: 0.5rem;
    // border-left: 2px solid var(--color__tertiary);
    margin-left: space(1);
    height: 3rem;
    background: rgb(236, 240, 245);
  }
  .asd20-app-template__desktop-layout .asd20-app-template__sidebar {
    border-right: 2px solid var(--color__tertiary);
  }
  &__sidebar-title {
    border-bottom: 2px solid var(--color__tertiary);
    justify-content: space-between;
    min-height: 4rem !important;
    .asd20-badge {
      margin: 0 0 0 -5rem !important;
      display: none;
    }
  }
  .asd20-select-input {
    margin-bottom: 0;
    width: 80vw;
    &__overlay-label {
      font-family: var(--website-typography__font-family-headlines);
      font-weight: bold;
      font-size: 1.25em;
      height: 4rem;
    }
  }
  .asd20-select-input__field-wrapper {
    justify-content: flex-start;
  }

  .asd20-event-list {
    width: 100%;
    padding: space(0.25) space(0.5) space(0) space(1);
    .asd20-list {
      .asd20-viewport {
        max-height: 70vh !important;
      }
    }
  }
  .asd20-event-modal__header {
    min-height: 300px;
  }

  .asd20-modal {
    max-width: 640px !important;
    p:first-of-type {
      margin-top: space(-0.5);
    }
  }

  .pdf-calendar-list {
    .pdf-button {
      width: 80%;
      margin: space(1) auto;
    }
    .calendar-description {
      display: flex;
      justify-content: space-between;
      font-family: var(--website-typography__font-family-headlines);
      align-items: center;
      margin: space(1) 0;
      p {
        margin: 0;
      }
      .print-view-buttons {
        display: flex;
        justify-content: flex-end;
        width: min-content;
        gap: 1rem;
        .pdf-button {
          width: min-content;
          margin: 0;
        }
      }
    }
  }
  main {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    .month-toolbar {
      flex-shrink: 0;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      border-bottom: 1px solid asd20-swatch('background-alt');
      align-items: center;
      background: var(--color__accent-t90);
      .month-toolbar-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .asd20-checkbox.asd20-checkbox__no-icon label:before {
        width: 2rem;
        height: 1rem;
      }
      .asd20-checkbox.asd20-checkbox__no-icon label:after {
        width: 1rem;
        height: 1rem;
        margin-left: -1rem;
        transform: translate3d(-1rem, 0, 0);
      }
      .asd20-checkbox.asd20-checkbox__no-icon input:checked + label:after {
        transform: translate3d(0, 0, 0);
      }

      .viewToggle {
        width: inherit;
        margin-left: auto;
        span:nth-of-type(1) {
          text-align: right;
          font-size: 0.65rem;
          font-family: var(--website-typography__font-family-headlines);
          width: min-content;
        }
        .asd20-checkbox__description {
          order: 4;
          font-size: 0.65rem;
          font-family: var(--website-typography__font-family-headlines);
          margin-left: 0.75rem;
          width: min-content;
        }
      }
    }
    .calendar-wrap {
      position: relative;
      flex-grow: 1;
      display: flex;
      .asd20-calendar__weekdays {
        display: none;
      }
      .asd20-calendar__days {
        flex-shrink: 0;
        height: 50%;
        .weekday {
          background: var(--color__accent-t80) !important;
        }
      }
    }
  }

  .footer-actions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: space(0.5);
    margin-top: auto;
    .asd20-button {
      width: 30%;
      flex-direction: column;
      text-align: center;
      margin: 0;
    }
    .asd20-button--horizontal {
      .asd20-button__label {
        margin-left: 0;
      }
    }
  }
  .categorized-list {
    font-family: var(--website-typography__font-family-headlines);
    h3 {
      padding-left: space(0.5);
      font-size: 1em;
    }
    .asd20-accordion__content {
      padding: 0;
    }
  }
  .subscriptionActions {
    display: flex;
    margin: space(0.5) 0;
  }
  .subscribe {
    border: 2px solid darkblue;
    margin-right: 1rem;
    .asd20-list-item__content {
      .asd20-list-item__label {
        font-size: 0.75rem;
      }
    }
  }
  .copy-to-clipboard {
    // width: min-content;
    width: 5rem;
    text-align: center;
    justify-content: center;
    .asd20-button__label {
      font-size: 0.75rem;
    }
  }
  .copy-to-clipboard-copied {
    background: darkblue;
    color: white !important;
  }
  .annual-calendar-modal .asd20-modal {
    max-width: 100% !important;
    width: 100vw !important;
    // height: 80vw !important;
    .annual-calendar-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      .title-and-logo {
        display: none;
      }
      .asd20-district-logo,
      .asd20-logo {
        display: none;
      }
      .print-controls {
        display: flex;
        .print-button,
        .poster-print-button {
          margin: space(0.25);
          padding: space(0.25);
          .asd20-button__label {
            margin-right: space(0.25);
          }
        }
      }
    }
    .asd20-annual-calendar {
      max-width: 100% !important;
      width: 100vw !important;
      height: 100vw !important;
    }
    .bottom-logo {
      display: none;
    }
  }
}

@media (min-width: 768px) {
  .asd20-app-template {
    .asd20-event-list {
      padding: space(0.5) space(0.5) space(0) space(1);
      .asd20-list {
        .asd20-viewport {
          max-height: 75vh !important;
        }
      }
    }
    main {
      .month-toolbar {
        display: block;
        .viewToggle {
          position: absolute;
          top: 1rem;
          right: 0;
          width: inherit;
          span:nth-of-type(1) {
            text-align: right;
            font-size: 0.75rem;
            font-family: var(--website-typography__font-family-headlines);
            width: inherit;
          }
          .asd20-checkbox__description {
            order: 4;
            font-size: 0.75rem;
            font-family: var(--website-typography__font-family-headlines);
            margin-left: 0.75rem;
            width: inherit;
          }
        }
      }
      .calendar-wrap {
        .asd20-calendar__weekdays {
          display: flex;
        }
        .asd20-calendar__days {
          height: 100%;
          flex: 1 1 auto;
          height: auto;
        }
      }
    }
    .footer-actions {
      padding: space(1);
    }
    .asd20-select-input {
      width: inherit !important;
      // margin: space(0.5) 0 0 0 !important;
      // padding-bottom: space(0.5);
      &__overlay-label {
        height: 3rem;
      }
    }
    .copy-to-clipboard {
      width: 10rem;
      text-align: center;
      justify-content: center;
    }
  }

  .asd20-app-template__toolbar {
    .asd20-search-field {
      display: none;
    }
    .asd20-button {
      .asd20-button__label {
        display: block !important;
      }
      .asd20-badge {
        top: 0;
        right: -0.5rem;
      }
    }
    // .asd20-button:nth-child(3) {
    //   border-left: none;
    // }
    .month {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
      border-left: 1px solid asd20-swatch('background-alt');
      padding: 0 space(0.5) 0 0;
    }
  }
}
@media (min-width: 1024px) {
  .asd20-app-template {
    .asd20-app-template__toolbar {
      #next-button,
      #previous-button {
        display: flex;
      }
      .year {
        display: block;
      }
    }
    main {
      .month-toolbar {
        .viewToggle {
          top: 1.125rem;
        }
      }
    }
    .annual-calendar-modal .asd20-modal {
      .annual-calendar-controls {
        .print-controls {
          .print-button,
          .poster-print-button {
            margin: space(0.5);
            padding: space(0.5);
          }
        }
      }
    }
    .categorized-list h3 {
      font-size: 1rem;
    }
    .asd20-accordion__content {
      padding: 0;
    }
  }
}

@media print {
  #print-container {
    display: block !important;
  }

  body > *:not(#print-container) {
    display: none !important;
  }

  #print-container .annual-calendar__grid {
    display: grid !important;
    grid-template-columns: repeat(6, 1fr) !important;
    grid-template-rows: repeat(2, auto) !important;
    gap: 0.125in !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    .asd20-calendar__weekdays {
      font-size: 0.75rem !important;
      height: auto !important;
    }
  }

  #print-container .annual-calendar__month {
    padding: 0 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  #print-container .annual-calendar__month h2 {
    font-size: 0.85rem !important;
    margin: 0.25rem 0 !important;
  }

  #print-container .asd20-calendar__day {
    // min-height: 1.25rem !important;
    margin: 0 0 0.125rem 0 !important;
  }

  #print-container .asd20-calendar__date .day {
    font-size: 0.5rem !important;
  }

  #print-container .monthly-events {
    font-size: 0.5rem !important;
    margin: 0.25rem 0 !important;
  }

  #print-container .monthly-events .event-item {
    padding-left: 0 !important;
    margin-bottom: 0.125rem !important;
  }

  #print-container .calendar-key {
    display: flex !important;
    font-size: 0.5rem !important;
    flex-direction: row !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    .dot {
      width: 8px;
      height: 8px;
    }
  }

  #print-container .calendar-key__item {
    margin-right: 1rem !important;
    margin-bottom: 0.25rem;
  }

  #print-container .address-footer {
    font-size: 0.5rem !important;
  }

  #print-container .bottom-logo {
    display: block !important;
    position: absolute !important;
    bottom: 1rem !important;
    right: 1rem !important;
    min-width: 15rem !important;
  }
  #print-container .asd20-calendar__days {
    display: grid !important;
    grid-template-columns: repeat(
      7,
      1fr
    ) !important; // You wanted 5 days per row in annual view
    grid-auto-rows: 0.25in !important;
    gap: 0in !important;
    height: auto !important;
    width: 100% !important;
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  #print-container .asd20-calendar__day {
    width: 100% !important;
    min-height: 1.25rem !important;
    box-sizing: border-box !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    background-color: var(--website-page__alternate-background-t70) !important;
    border: 1px solid var(--color__tertiary) !important;
    margin: 0 !important;
  }
  @page {
    size: letter landscape;
    margin: 0.25in;
  }
}

@media print {
  .poster-print {
    .annual-calendar__grid {
      grid-template-columns: repeat(3, 1fr) !important;
      grid-template-rows: repeat(4, auto) !important;
      gap: 0.5in !important;
    }

    .annual-calendar__month {
      padding: 0.5in;
      font-size: 1.25rem;
      h2 {
        font-size: 1.5rem;
      }
      .monthly-events {
        font-size: 1rem;
      }
    }

    .calendar-key,
    .address-footer {
      font-size: 1rem;
      padding: 0.5in;
    }
  }

  // @page {
  //   size: ledger portrait;
  //   margin: 0.5in;
  // }
}
@media print {
  body > *:not(#print-container) {
    display: none !important;
  }

  #print-container {
    display: block !important;
  }
}

@media print {
  p,
  span {
    font-family: var(--website-typography__font-family-body);
  }
  h1,
  h2,
  h3,
  h4 {
    font-family: var(--website-typography__font-family-headlines);
  }
}
@page {
  size: letter landscape;
  margin: 0.25in;
}
</style>
